import { PlusIcon } from '@heroicons/react/20/solid'
import { useState } from 'react'
import { AreaTreemap } from '../catalyst/area-treemap'
import { Button } from '../catalyst/button'
import { Heading } from '../catalyst/heading'
import { Text } from '../catalyst/text'
import { formatArea, orgAreaUnit } from '../lib/area'
import { useAuth } from '../lib/auth'
import { useFarms } from '../lib/farms'
import { FarmFormDialog } from './farm-form-dialog'
import { Shell } from './shell'

function StatTile({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg bg-white p-5 shadow-xs ring-1 ring-zinc-950/5 dark:bg-zinc-900 dark:ring-white/10">
      <Text>{label}</Text>
      <div className="mt-1 text-2xl font-semibold text-zinc-950 dark:text-white">{value}</div>
    </div>
  )
}

export function DashboardPage() {
  const { org } = useAuth()
  const areaUnit = orgAreaUnit(org)
  const canEditFarms = org?.role === 'OWNER'
  const { farms, summaryByFarm, loading, error } = useFarms()
  const [addFarmOpen, setAddFarmOpen] = useState(false)

  const fieldCount = (farmId: string) => (summaryByFarm[farmId] ?? []).reduce((sum, row) => sum + row.fields, 0)
  const acreage = (farmId: string) =>
    (summaryByFarm[farmId] ?? []).reduce((sum, row) => sum + row.totalAreaM2, 0)

  const totalFields = farms.reduce((sum, farm) => sum + fieldCount(farm.id), 0)
  const totalArea = farms.reduce((sum, farm) => sum + acreage(farm.id), 0)

  return (
    <Shell>
      <div className="flex items-center justify-between gap-4">
        <div>
          <Heading>Farms</Heading>
          <Text>Your farms and planted fields.</Text>
        </div>
        {canEditFarms && (
          <Button onClick={() => setAddFarmOpen(true)}>
            <PlusIcon />
            Add farm
          </Button>
        )}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatTile label="Farms" value={farms.length} />
        <StatTile label="Fields" value={totalFields} />
        <StatTile label={areaUnit === 'acre' ? 'Acreage' : 'Area'} value={formatArea(totalArea, areaUnit)} />
      </div>

      <div className="mt-8">
        {error ? (
          <div className="rounded-lg border border-dashed border-red-300 p-10 text-center dark:border-red-900">
            <Text>{error}</Text>
          </div>
        ) : loading ? (
          <div className="rounded-lg border border-dashed border-zinc-950/15 p-10 text-center dark:border-white/15">
            <Text>Loading farms…</Text>
          </div>
        ) : farms.length === 0 ? (
          <div className="rounded-lg border border-dashed border-zinc-950/15 p-10 text-center dark:border-white/15">
            <Text>{canEditFarms ? 'No farms yet. Add your first farm to get started.' : 'No farms yet. Ask the account owner to add one.'}</Text>
          </div>
        ) : (
          <>
            <AreaTreemap
              items={farms.map((farm) => ({
                id: farm.id,
                label: farm.name,
                detail: `${fieldCount(farm.id)} ${fieldCount(farm.id) === 1 ? 'field' : 'fields'} · ${formatArea(acreage(farm.id), areaUnit)}`,
                value: acreage(farm.id),
                href: `/farms/${farm.id}`,
              }))}
            />
            <Text className="mt-2">Tile size is proportional to area. Select a farm to see its fields.</Text>
          </>
        )}
      </div>

      {canEditFarms && <FarmFormDialog key={`add-farm-${addFarmOpen}`} open={addFarmOpen} onClose={() => setAddFarmOpen(false)} />}
    </Shell>
  )
}
